import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Play, Layers, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradientMap = {
  cabin:    'from-emerald-500 to-emerald-600',
  aviation: 'from-sky-500 to-sky-600',
  pax:      'from-purple-500 to-purple-600',
  general:  'from-amber-500 to-amber-600',
};

function CourseCardSkeleton() {
  return (
    <div className="card-duo p-5 flex items-center gap-4">
      <Skeleton className="w-16 h-16 rounded-2xl shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3 rounded" />
        <Skeleton className="h-5 w-2/3 rounded" />
        <Skeleton className="h-3 w-full rounded-full" />
      </div>
      <Skeleton className="w-24 h-10 rounded-xl shrink-0" />
    </div>
  );
}

function EnrolledCourseCard({ enrollment }) {
  const { t, getField } = useLanguage();

  const course = enrollment.course;
  const courseId = course?.course_id || course?._id || course?.id;
  const courseTitle = getField(course, 'title');
  const instructorName = course?.instructor_name || course?.instructor || '';

  const totalLessons =
    enrollment?.lessons?.length
    || enrollment?.total_lessons
    || enrollment?.lesson_count
    || enrollment?.course?.lessons_count
    || enrollment?.course?.lesson_count
    || enrollment?.course?.lessons?.length
    || course?.lessons_count
    || course?.lesson_count
    || course?.lessons?.length
    || 0;

  const completedCount =
    enrollment?.completed_lesson_ids?.length
    || enrollment?.completed_lessons?.length
    || enrollment?.completed_lessons_count
    || enrollment?.completed_lessons
    || enrollment?.lessons_completed
    || 0;

  const pct =
    enrollment?.progress != null && Number.isFinite(Number(enrollment.progress))
      ? Math.round(Number(enrollment.progress))
      : (totalLessons > 0
          ? Math.round((completedCount / totalLessons) * 100)
          : 0);

  const nextLesson = enrollment.next_lesson || null;
  const nextLessonId = nextLesson
    ? (nextLesson.lesson_id || nextLesson._id || nextLesson.id)
    : null;

  const continueUrl = nextLessonId
    ? `/lesson/${courseId}/${nextLessonId}`
    : `/courses/${courseId}`;

  const courseType = course?.course_type || course?.category || 'general';
  const gradient = gradientMap[courseType] || gradientMap.general;

  const ctaLabel = completedCount > 0
    ? t('myCourses.continue')
    : t('myCourses.start');

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="card-duo p-5"
      data-testid="my-course-card"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        {/* Icon */}
        <div
          className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0 border-2 border-white shadow-md`}
          aria-hidden="true"
        >
          <BookOpen size={28} strokeWidth={1.8} className="text-white" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          {instructorName && (
            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-0.5">
              {instructorName}
            </p>
          )}
          <h2
            className="font-duo-display text-base text-[var(--text-primary)] leading-snug mb-2 line-clamp-2 overflow-hidden"
            title={courseTitle}
          >
            {courseTitle}
          </h2>

          {/* Progress bar */}
          <div className="flex items-center gap-2 mb-1">
            <div
              className="flex-1 h-2.5 rounded-full bg-gray-100 border border-gray-200 overflow-hidden"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-[var(--brand-success)] transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-bold text-[var(--text-secondary)] shrink-0 w-10 text-right">
              {pct}%
            </span>
          </div>
          <p className="text-[10px] text-gray-400">
            {t('myCourses.lessons')
              .replace('{{completed}}', completedCount)
              .replace('{{total}}', totalLessons || '—')}
          </p>
        </div>

        {/* CTA */}
        <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
          <Link
            to={continueUrl}
            className="btn-duo-primary inline-flex items-center gap-2 w-full sm:w-auto justify-center"
            data-testid="my-course-continue-btn"
          >
            <Play size={15} strokeWidth={2.5} aria-hidden="true" />
            {ctaLabel}
          </Link>
          <Link
            to={`/courses/${courseId}`}
            className="btn-duo-ghost inline-flex items-center gap-1 shrink-0"
            aria-label="Course details"
          >
            <ChevronRight size={16} strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default function MyCoursesPage() {
  const { authHeaders } = useAuth();
  const { t } = useLanguage();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await axios.get(`${API}/enrollments`, { headers: authHeaders() });
        if (!cancelled) {
          setEnrollments(res.data.enrollments || []);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validEnrollments = enrollments.filter(e => e.course);

  return (
    <div className="min-h-screen duo-bg relative overflow-hidden">
      {/* Background decorations */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.10] blur-3xl bg-[var(--brand-success,#10b981)]" />
        <div className="absolute -bottom-24 -left-24 w-[480px] h-[480px] rounded-full opacity-[0.08] blur-3xl bg-[var(--brand-primary,#0EA5E9)]" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <pattern id="my-courses-dots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.5" fill="#0EA5E9" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#my-courses-dots)" />
        </svg>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[var(--duo-sky)] flex items-center justify-center shrink-0">
            <BookOpen size={20} strokeWidth={2} className="text-[var(--duo-blue)]" aria-hidden="true" />
          </div>
          <div>
            <h1 className="font-duo-display text-2xl text-[var(--text-primary)]">
              {t('myCourses.title')}
            </h1>
            {!loading && validEnrollments.length > 0 && (
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                {validEnrollments.length} {validEnrollments.length === 1 ? 'kurs' : 'kurs'}
              </p>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="space-y-4">
            <CourseCardSkeleton />
            <CourseCardSkeleton />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="card-duo p-8 text-center">
            <p className="text-[var(--text-secondary)] mb-4">{t('dashboard.error.sub')}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-duo-primary"
            >
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && validEnrollments.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-duo p-12 text-center"
            data-testid="my-courses-empty"
          >
            <div className="w-16 h-16 rounded-2xl bg-[var(--duo-sky)] flex items-center justify-center mx-auto mb-4">
              <Layers size={32} strokeWidth={1.5} className="text-[var(--duo-blue)]" aria-hidden="true" />
            </div>
            <h2 className="font-duo-display text-xl text-[var(--text-primary)] mb-2">
              {t('myCourses.empty.title')}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mb-6">
              {t('myCourses.empty.sub')}
            </p>
            <Link to="/courses" className="btn-duo-primary inline-flex items-center gap-2">
              <Layers size={16} strokeWidth={2.5} aria-hidden="true" />
              {t('myCourses.empty.cta')}
            </Link>
          </motion.div>
        )}

        {/* Course list */}
        {!loading && !error && validEnrollments.length > 0 && (
          <div className="space-y-4">
            {validEnrollments.map((enrollment, idx) => (
              <EnrolledCourseCard
                key={enrollment._id || enrollment.enrollment_id || idx}
                enrollment={enrollment}
              />
            ))}

            {/* Browse more */}
            <div className="pt-4 text-center">
              <Link
                to="/courses"
                className="btn-duo-ghost inline-flex items-center gap-2 text-sm"
              >
                <Layers size={16} strokeWidth={2.5} aria-hidden="true" />
                {t('myCourses.empty.cta')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
